import React, {useMemo} from 'react';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {TPAComponentsProvider, Accordion, Text, TextTypography} from '@wix/wix-ui-tpa/cssVars';
import {pageTheme} from '../../../common/components/WithThemeOverride/pageTheme';
import {Header} from '../Header/Header';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {usePoliciesControllerProps} from '../Widget/PoliciesControllerContext';
import {classes} from './PoliciesPage.st.css';
import {getDirValue} from '../../../domain/utils/getDirValue';
import {SanitizeInnerHTMLWrapper} from '@wix/ecom-platform-storefront-common';
import {CheckoutSettingsModel} from '../../../domain/models/checkout/checkoutSettings/CheckoutSettings.model';

export enum PoliciesPageDataHook {
  root = 'PoliciesPageDataHook.root',
  header = 'PoliciesPageDataHook.header',
  main = 'PoliciesPageDataHook.main',
  accordion = 'PoliciesPageDataHook.accordion',
}

const getPolicies = (checkoutSettings: CheckoutSettingsModel, localeKeys: ReturnType<typeof useLocaleKeys>) => {
  return [
    {
      id: 'termsAndConditions',
      enabled: checkoutSettings?.termsAndConditions?.enabled,
      title: localeKeys.checkout.terms_and_condition_modal.title(),
      content: checkoutSettings?.termsAndConditions?.value,
    },
    {
      id: 'privacyPolicy',
      enabled: checkoutSettings?.privacyPolicy?.enabled,
      title: localeKeys.checkout.privacy_policy_modal.title(),
      content: checkoutSettings?.privacyPolicy?.value,
    },
    {
      id: 'returnPolicy',
      enabled: checkoutSettings?.returnPolicy?.enabled,
      title: localeKeys.checkout.refund_cancellation_modal.title(),
      content: checkoutSettings?.returnPolicy?.value,
    },
    {
      id: 'digitalPolicy',
      enabled: checkoutSettings?.digitalPolicy?.enabled,
      title: localeKeys.checkout.digital_item_policy_modal.title(),
      content: checkoutSettings?.digitalPolicy?.value,
    },
    {
      id: 'customPolicy',
      enabled: checkoutSettings?.customPolicy?.enabled,
      title: checkoutSettings?.customPolicy?.title,
      content: checkoutSettings?.customPolicy?.value,
    },
  ].filter((policy) => policy.enabled);
};

const PoliciesPage = () => {
  const {isRTL, isMobile} = useEnvironment();
  const {
    checkoutSettingsStore: {checkoutSettings},
    focusedPolicyId,
  } = usePoliciesControllerProps();

  const localKeys = useLocaleKeys();
  const policiesTitle = localKeys.checkout.policies.header();
  const policies = getPolicies(checkoutSettings, localKeys);

  const initiallyExpanded = useMemo(() => {
    if (focusedPolicyId) {
      const policyExists = policies.some((policy) => policy.id === focusedPolicyId);
      if (policyExists) {
        return focusedPolicyId;
      }
    }
    return policies[0]?.id;
  }, [focusedPolicyId, policies]);

  return (
    <div style={pageTheme} className={isMobile ? 'mobile-view' : undefined}>
      <TPAComponentsProvider value={{rtl: isRTL}}>
        <div data-hook={PoliciesPageDataHook.root} className={classes.root} dir={getDirValue(isRTL)}>
          <Header
            dataHook={PoliciesPageDataHook.header}
            titleSuffixOverride={policiesTitle}
            checkoutSettings={checkoutSettings}
          />
          <main data-hook={PoliciesPageDataHook.main} className={classes.main}>
            {policies.length > 0 && (
              <Accordion data-hook={PoliciesPageDataHook.accordion} initiallyExpanded={initiallyExpanded}>
                {policies.map((policy) => (
                  <Accordion.Item key={policy.id} id={policy.id} title={policy.title}>
                    <Text typography={TextTypography.runningText} className={classes.policyContent}>
                      <SanitizeInnerHTMLWrapper innerHtml={policy.content}>
                        <div />
                      </SanitizeInnerHTMLWrapper>
                    </Text>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </main>
        </div>
      </TPAComponentsProvider>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default PoliciesPage;
